import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw, Archive, RotateCcw, Check, X } from "lucide-react";
import { seguimientoApi } from "./api";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { cn } from "@/lib/utils/cn";
import { Badge, FieldLabel } from "./ui";
import type { BusinessArea } from "./types";

const DEFAULT_COLOR = "#6366F1";

/** Pestaña de configuración de las áreas de negocio (editable + color). */
export function AreasPane() {
  const qc = useQueryClient();
  const { push } = useToast();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [color, setColor] = useState(DEFAULT_COLOR);

  // ?all=1 para incluir archivadas (gestión completa).
  const areasQuery = useQuery({
    queryKey: ["seg", "businessAreas", "all"],
    queryFn: () => seguimientoApi.businessAreas({ all: 1 }),
  });
  // Conteo de tareas por área (de métricas) para mostrar uso.
  const metricsQuery = useQuery({ queryKey: ["seg", "metrics"], queryFn: seguimientoApi.metrics });
  const countById = new Map(
    (metricsQuery.data?.byBusinessArea ?? []).map((a) => [a.businessAreaId, a.taskCount]),
  );

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["seg", "businessAreas"] });
    qc.invalidateQueries({ queryKey: ["seg", "tasks"] });
    qc.invalidateQueries({ queryKey: ["seg", "metrics"] });
  };

  const create = useMutation({
    mutationFn: () =>
      seguimientoApi.createBusinessArea({
        name: name.trim(),
        colorHex: color,
        orderIndex: areasQuery.data?.length ?? 0,
      }),
    onSuccess: () => {
      push({ kind: "success", message: "Área creada." });
      setName("");
      setColor(DEFAULT_COLOR);
      setCreating(false);
      invalidate();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo crear." }),
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-fg-muted">
          Áreas de negocio para etiquetar y colorear las tareas. Definí las que potencia el equipo.
        </p>
        <button
          onClick={() => areasQuery.refetch()}
          className="nx-btn-secondary ml-auto"
          disabled={areasQuery.isFetching}
        >
          <RefreshCw size={14} className={areasQuery.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
        <button onClick={() => setCreating((v) => !v)} className="nx-btn-primary">
          <Plus size={14} /> Nueva área
        </button>
      </div>

      {creating && (
        <div className="nx-card p-3 flex flex-wrap items-end gap-3">
          <div>
            <FieldLabel>Color</FieldLabel>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-9 w-12 rounded border border-border bg-bg-card cursor-pointer"
              aria-label="Color del área"
            />
          </div>
          <div className="flex-1 min-w-[180px]">
            <FieldLabel>Nombre</FieldLabel>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && name.trim() && create.mutate()}
              className="nx-input"
              placeholder="Ej: Producto"
              autoFocus
            />
          </div>
          <button
            onClick={() => create.mutate()}
            disabled={!name.trim() || create.isPending}
            className="nx-btn-primary"
          >
            <Check size={14} /> Crear
          </button>
          <button onClick={() => setCreating(false)} className="nx-btn-secondary">
            <X size={14} /> Cancelar
          </button>
        </div>
      )}

      {areasQuery.isLoading ? (
        <LoadingBlock label="Cargando áreas…" />
      ) : areasQuery.isError ? (
        <ErrorBlock onRetry={() => areasQuery.refetch()} />
      ) : (areasQuery.data ?? []).length === 0 ? (
        <p className="text-sm text-fg-subtle">Todavía no hay áreas. Creá la primera.</p>
      ) : (
        <div className="nx-card overflow-hidden">
          <table className="nx-table">
            <thead>
              <tr>
                <th style={{ width: 48 }}></th>
                <th>Área</th>
                <th className="text-right">Tareas</th>
                <th>Estado</th>
                <th style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {(areasQuery.data ?? []).map((a) => (
                <AreaRow
                  key={a.id}
                  area={a}
                  taskCount={countById.get(a.id) ?? 0}
                  onChanged={invalidate}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AreaRow({
  area,
  taskCount,
  onChanged,
}: {
  area: BusinessArea;
  taskCount: number;
  onChanged: () => void;
}) {
  const { push } = useToast();
  const [draftName, setDraftName] = useState(area.name);

  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) => seguimientoApi.updateBusinessArea(area.id, body),
    onSuccess: () => onChanged(),
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo guardar." }),
  });

  const archive = useMutation({
    mutationFn: () => seguimientoApi.deleteBusinessArea(area.id),
    onSuccess: () => {
      push({ kind: "success", message: "Área archivada." });
      onChanged();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo archivar." }),
  });

  const saveName = () => {
    const v = draftName.trim();
    if (!v || v === area.name) {
      setDraftName(area.name);
      return;
    }
    update.mutate({ name: v });
  };

  return (
    <tr className={cn(!area.active && "opacity-60")}>
      <td>
        <input
          type="color"
          value={area.colorHex}
          onChange={(e) => update.mutate({ colorHex: e.target.value })}
          className="h-7 w-9 rounded border border-border bg-bg-card cursor-pointer align-middle"
          aria-label={`Color de ${area.name}`}
        />
      </td>
      <td>
        <input
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          onBlur={saveName}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            if (e.key === "Escape") setDraftName(area.name);
          }}
          className="nx-input"
        />
      </td>
      <td className="text-right tabular-nums text-fg-muted">{taskCount}</td>
      <td>
        {area.active ? (
          <Badge tone="success">Activa</Badge>
        ) : (
          <Badge tone="neutral">Archivada</Badge>
        )}
      </td>
      <td className="text-right">
        {area.active ? (
          <button
            onClick={async () => {
              const msg =
                taskCount > 0
                  ? `${taskCount} tarea(s) usan esta área. Se archiva (no se borra) para no perder su color/nombre en el historial.`
                  : "Se archiva el área.";
              const ok = await confirmAction({
                title: `Archivar "${area.name}"`,
                message: msg,
                confirmLabel: "Archivar",
                tone: "danger",
              });
              if (ok) archive.mutate();
            }}
            className="nx-btn-ghost p-1.5"
            aria-label="Archivar"
            disabled={archive.isPending}
          >
            <Archive size={14} />
          </button>
        ) : (
          <button
            onClick={() => update.mutate({ active: true })}
            className="nx-btn-ghost p-1.5"
            aria-label="Restaurar"
            disabled={update.isPending}
          >
            <RotateCcw size={14} />
          </button>
        )}
      </td>
    </tr>
  );
}
